import './index.scoped.scss';
import { useEffect, useCallback, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Icon from 'src/components/Icon';
import useTab from 'src/containers/Product/useTab';
import useMonth, { MONTH_TYPE } from 'src/containers/Product/My/useMonth';
import Button from 'src/components/Button';
import { userProducts } from 'src/services/api';
import useCustomHistory from 'src/hooks/useCustomHistory';
import { useUser } from 'src/UserContext';
import { Trans, useTranslation } from 'react-i18next';
import { isRemain, isSubscription } from 'src/services/util';
import { PRODUCT_CATEGORY, PRODUCT_REGION, USER_PRODUCT_STATUS } from 'src/constants/product';
import MyDeactivatedCard from './DeactivatedCard';
import MyActivatedCard from './ActivatedCard';
import MyEndDateCard from './EndDateCard';
import { KY_BIXBY_NAME, KY_KARAOKE_NAME } from 'src/constants/ky';
import { useModal } from 'src/ModalContext';
import MySubCreatedCard from './SubCreatedCard';

dayjs.extend(utc);

export default function My(): JSX.Element {
  const { t } = useTranslation();
  const history = useCustomHistory();
  const { tab, TabComponent } = useTab();
  const { setMonth, MonthComponent } = useMonth({
    onClick: (month: number) => {
      getList(month);
    },
  });
  const [list, setList] = useState<CheckoutUserProduct[]>([]);
  const { isInit, isLogin, userInfo } = useUser();
  const { errorModal } = useModal();

  const getList = useCallback(
    (month: number) => {
      userProducts({
        searchStartDate: dayjs(dayjs().add(-1 * month, 'month'))
          .utc()
          .format(),
        category: tab,
      })
        .then((res) => {
          if (res && res.products && res.products.length > 0) {
            res.products.forEach((obj: CheckoutUserProduct) => {
              if (isSubscription(obj.status)) {
                obj.endDate = dayjs(obj.endDate).add(-12, 'hour').utc().format();
              }
            });
            setList(res.products);
          } else {
            setList([]);
          }
        })
        .catch(errorModal);
    },
    [tab],
  );

  const isSomeIng = useCallback(() => {
    return list.some((item: CheckoutUserProduct) => {
      if (item.status === USER_PRODUCT_STATUS.SUB_CREATED) {
        return true;
      }
      if (
        ![
          USER_PRODUCT_STATUS.SUB_CREATED,
          USER_PRODUCT_STATUS.SUB_ACTIVATED,
          USER_PRODUCT_STATUS.SUB_TERMINATED,
          USER_PRODUCT_STATUS.SINGLE,
        ].includes(item.status)
      ) {
        return false;
      }

      if (item.endDate && isRemain(item.endDate)) {
        return true;
      }

      return false;
    });
  }, [list]);

  useEffect(() => {
    if (!isInit) return;
    if (isLogin) {
      setMonth(MONTH_TYPE.MONTH3); // 초기화
      getList(MONTH_TYPE.MONTH3);
    }
  }, [tab, isInit, isLogin]);
  return (
    <div className="flex flex-col min-h-1/2">
      <div className="">
        <div className="container">
          <div className="flex justify-between mt-6 h-7">
            <div className="N_20_R text-black">{t('product/my/title')}</div>
            <div
              className="flex mt-1"
              onClick={() => {
                history.push('/product/auto-payment');
              }}
            >
              <div className="N_12_M text-brown-grey underline cursor-pointer">{t('product/my/auto_payment_manage')}</div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="mt-4 -mx-6">{userInfo.country === PRODUCT_REGION.KR && TabComponent}</div>
        </div>
      </div>
      <div className="bg-light-gray-1 flex-grow">
        <div className="container pt-6 pb-10">
          <div className="pb-1">{MonthComponent}</div>
          {(!list || list.length === 0 || !isSomeIng()) && (
            <div className="mt-3">
              <div className="border border-ice-blue rounded bg-white min-h-40 px-5 pt-9 pb-5 flex flex-col justify-between">
                <div className="N_14_R text-brown-grey text-center">
                  <Trans
                    i18nKey="product/my/not_product_message"
                    values={{
                      service: tab === PRODUCT_CATEGORY.BIXBY ? KY_BIXBY_NAME : KY_KARAOKE_NAME,
                    }}
                  />
                </div>
                <div className="mt-7">
                  <Button
                    title={t('home/greeting_purchase_button_title')}
                    theme="sub"
                    onClick={() => {
                      history.push('/product/purchase', {
                        tab,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          {list &&
            list.map((item: CheckoutUserProduct) => {
              switch (item.status) {
                case USER_PRODUCT_STATUS.SUB_CANCELLED:
                case USER_PRODUCT_STATUS.SINGLE_CANCELLED:
                case USER_PRODUCT_STATUS.SUB_DEACTIVATED:
                  return <MyDeactivatedCard key={item.id} item={item} />;

                case USER_PRODUCT_STATUS.SUB_CREATED:
                  return <MySubCreatedCard key={item.id} item={item} />;

                default:
                  if (isRemain(item.endDate)) {
                    return <MyActivatedCard key={item.id} item={item} />;
                  } else {
                    return <MyEndDateCard key={item.id} item={item} />;
                  }
              }
            })}
        </div>
      </div>
    </div>
  );
}
